import { type JsonObject, Struct } from "@bufbuild/protobuf";
import { type WebContext } from "@reboot-dev/reboot-web";
import {
	Commit,
	type CommitJSON,
	sendableCommit,
} from "@stepwisehq/prosemirror-collab-commit/collab-commit";
import { Mutex } from "es-toolkit";
import type { EditorState } from "prosemirror-state";

import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";
import {
	ApplyRequest,
	type ChangesResponse,
	type PageAuthority,
	PageAuthorityApplyAborted,
	Commit as RebootCommit,
} from "@moment/reboot-differential";

import { type EditorStateParams } from "../../prosemirror/editor-state.types";

const logger = new Logger("reboot-utils");

/**
 * Serialize JSON for Protobuf Struct, removing undefined values
 */
export function serializeForStruct(json: any): any {
	if (typeof json !== "object" || json === null) {
		return json;
	}
	if (Array.isArray(json)) {
		return json.map((j) => serializeForStruct(j));
	}
	return Object.fromEntries(
		Object.entries(json)
			.filter(([_key, value]) => value !== undefined)
			.map(([key, value]) => [key, serializeForStruct(value)])
	);
}

/**
 * Creates a sendToCollab callback that handles duplicate prevention and error handling.
 */
export function makeMdPageSendToCollabCommand(
	authority: PageAuthority.WeakReference,
	context: WebContext
): NonNullable<EditorStateParams["sendToCollab"]> {
	// Closure state for duplicate prevention.
	let lastSentCommitRef: string | null = null;

	const lock = new Mutex();

	return async (editorState: EditorState): Promise<void> => {
		await lock.acquire();

		let sendable: Commit | null = null;
		try {
			sendable = sendableCommit(editorState);
			if (!sendable) {
				return;
			}

			// Don't attempt to send the same commit multiple times.
			if (sendable.ref === lastSentCommitRef) {
				logger.debug("Skipping duplicate commit", {
					ref: sendable.ref,
					send: sendable,
					st: editorState.doc.textContent,
				});
				return;
			}

			const { commit } = await authority.apply(context, commitToApplyRequest(sendable));
			if (!commit || commit.version === undefined) {
				throw new Error("Commit is undefined or has an undefined version");
			}

			// Only update ref on successful send
			lastSentCommitRef = sendable.ref;
		} catch (e: any) {
			// PageAuthorityApplyAborted with code 3 means the commit was rejected (likely due to
			// conflict) This is not an error: we'll get the corrected state from the remote changes
			// stream.
			//
			// Lita says look at this if you get confuuuse:
			// https://grpc.github.io/grpc/core/md_doc_statuscodes.html
			if (e instanceof PageAuthorityApplyAborted && e.code === 3) {
				logger.debug("Commit rejected by authority (conflict)", { ref: sendable?.ref });
				return;
			}

			// Re-throw other errors.
			throw e;
		} finally {
			lock.release();
		}
	};
}

/**
 * Simple function that converts the Reboot `ChangesResponse` Protocol Buffer type
 * prosemirror-collab-commit's `Commit` type.
 */
export function changesResponseToCommits({ commits }: ChangesResponse): Commit[] {
	return commits.map((pbCommit) => {
		const steps = pbCommit.steps.map((step) => step.toJson() as JsonObject);
		// `CommitJSON` is compatible with `ChangesResponse#commits`, except we need to conver the
		// `steps` to `JsonObject[]`. Ugh/lol.
		const commit: CommitJSON = { ...pbCommit, steps };
		return Commit.FromJSON(schema, commit);
	});
}

export function commitToApplyRequest(_commit: Commit): ApplyRequest {
	const steps = _commit.steps.map((step) => Struct.fromJson(serializeForStruct(step.toJSON())));
	const commit = new RebootCommit({ ..._commit, steps });
	return new ApplyRequest({ commit });
}

/**
 * Converts a Reboot `PageAuthority` ref into a plain-old infinite stream of `Commit[]`.
 *
 * This callback is necessary because we constantly have to update the reactive read of
 * `PageAuthority#changes` to use the latest version.
 */
export function makeMdPageRemoteChangesCommand(
	authority: PageAuthority.WeakReference,
	context: WebContext,
	initialVersion: number
): NonNullable<EditorStateParams["remoteChanges"]> {
	return async function* (ops: { signal: AbortSignal }): AsyncGenerator<Commit[]> {
		const signal = ops.signal;

		let sinceVersion = initialVersion;
		const [stream, update] = await authority
			.reactively()
			.changes(context, { sinceVersion }, { signal });

		const seenRefs = new Set<string>();

		while (!signal.aborted) {
			const result = await stream.next();
			if (result.done || !result.value) {
				logger.info("Remote changes stream ended");
				break;
			}

			// Ensure that we don't receive the same commits multiple times.
			const _commits = changesResponseToCommits(result.value);
			const commits = _commits.filter(({ ref }) => !seenRefs.has(ref));

			// Update sinceVersion for the reactive stream.
			const lastCommit = commits.at(-1);
			if (lastCommit) {
				sinceVersion = lastCommit.version;
			}

			// Keep track of these commits to ensure we don't receive them again later.
			for (const commit of commits) {
				seenRefs.add(commit.ref);
			}

			// Yield unseen commits.
			if (commits.length > 0) {
				yield commits;

				// Update the stream to continue from the latest version
				update({ sinceVersion });
			}
		}
	};
}
